import { timer } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  skip,
  startWith,
  switchMap,
  takeUntil
} from 'rxjs/operators';

import type { EffectParams, WidgetEffectParams } from '@swf/core/store';
import { select } from '@swf/core/store';
import { canUseDOM } from '@swf/core/utils';

/**
 * Returns an observable that runs on page change.
 * It does not run on first widget mount
 */
export const pageChange = (actions: EffectParams['actions']) =>
  canUseDOM
    ? actions.pipe(
        filter(({ type }) => type === 'page/_change' || type === 'page/ready'),
        skip(1),
        filter(({ type }) => type === 'page/_change')
      )
    : null;

export const urlParamChange = (params: WidgetEffectParams<unknown>, urlParam: string) =>
  pageChange(params.actions)?.pipe(
    map(({ state }) => params.select.page.getUrlParams(state)[urlParam]),
    distinctUntilChanged()
  );

export const sportsUrlParamChange = (params: WidgetEffectParams<unknown>, urlParam: string) =>
  canUseDOM
    ? params.actions.pipe(
        // Filter actions to URL changes
        filter(({ type }) => type === 'page/_change' || type === 'page/ready'),

        // Derive URL parameter value
        map(({ state }) => params.select.page.getSportsUrlParams(state)[urlParam]),

        // Wait for changes to derived value
        distinctUntilChanged(),

        // Skip first widget mount (async logic should be handled by "preload")
        skip(1)
      )
    : null;

export const login = (actions: EffectParams['actions']) =>
  canUseDOM
    ? actions.pipe(
        filter(
          ({ type, state }) =>
            type === 'session/response' && select.session.isClientAuthenticated(state)
        )
      )
    : null;

export const logout = (actions: EffectParams['actions']) =>
  canUseDOM ? actions.pipe(filter(({ type }) => type === 'session/clear')) : null;

export const currencyChange = (actions: EffectParams['actions']) =>
  canUseDOM
    ? actions.pipe(
        distinctUntilChanged(
          (prev, curr) => select.currency.get(prev.state) === select.currency.get(curr.state)
        ),
        skip(1)
      )
    : null;

/*
 * Creates an interval which resets upon page change.
 */
export const widgetIntervalWithPageChangeReset = (
  params: WidgetEffectParams<unknown>,
  interval: number,
  initialDelay = 0
) =>
  pageChange(params.actions)?.pipe(
    startWith(true),
    switchMap(() => timer(initialDelay, interval)),
    takeUntil(params.widget.unmount)
  );
